import '@refinedev/antd/dist/reset.css'
import './app.css'

import {
  BarChartOutlined,
  CloudServerOutlined,
  ClusterOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  FileExclamationOutlined,
  GiftOutlined,
  GlobalOutlined,
  KeyOutlined,
  LinkOutlined,
  MailOutlined,
  NodeIndexOutlined,
  StopOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UsergroupAddOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import {
  ErrorComponent,
  ThemedLayout,
  useNotificationProvider,
} from '@refinedev/antd'
import {
  Authenticated,
  CanAccess,
  Refine,
  usePermissions,
} from '@refinedev/core'
import routerProvider, {
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router'
import { App as AntApp, ConfigProvider, Grid, Spin } from 'antd'
import { type ReactNode, useEffect } from 'react'
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import { hasAdminAccess } from '@/auth/capabilities'
import { saveReturnTo } from '@/auth/returnTo'
import { CommandPalette } from '@/components/command-palette/CommandPalette'
import { CustomSider } from '@/components/layout/Sidebar'
import { getThemeConfig } from '@/config/theme'
import { useThemeMode } from '@/hooks/useThemeMode'
import AccessDeniedPage from '@/pages/access-denied'
import AuthCallbackPage from '@/pages/auth-callback'
import BusinessPage from '@/pages/business'
import ClustersListPage from '@/pages/clusters/list'
import ClusterShowPage from '@/pages/clusters/show'
import ComputeListPage from '@/pages/compute/list'
import ComputeShowPage from '@/pages/compute/show'
import DashboardPage from '@/pages/dashboard'
import AkashBlocksListPage from '@/pages/gpu/akash-blocks/list'
import GpuInstancesListPage from '@/pages/gpu/instances/list'
import GpuInstanceShowPage from '@/pages/gpu/instances/show'
import GpuProvidersListPage from '@/pages/gpu/list'
import GpuReportsListPage from '@/pages/gpu/reports/list'
import GpuProviderShowPage from '@/pages/gpu/show'
import ImagesRoute from '@/pages/images/route'
import InvitationsListPage from '@/pages/invitations/list'
import LoginPage from '@/pages/login'
import NetworkListPage from '@/pages/network/list'
import NetworkShowPage from '@/pages/network/show'
import PaymentMethodsListPage from '@/pages/payment-methods/list'
import PaymentsListPage from '@/pages/payments/list'
import PaymentShowPage from '@/pages/payments/show'
import PricesListPage from '@/pages/prices/list'
import PromoCodesListPage from '@/pages/promo-codes/list'
import PromoCodeShowPage from '@/pages/promo-codes/show'
import RoleCreatePage from '@/pages/roles/create'
import { getDocumentTitle } from '@/pages/roles/documentTitle'
import RolesListPage from '@/pages/roles/list'
import RoleShowPage from '@/pages/roles/show'
import StorageListPage from '@/pages/storage/list'
import StorageShowPage from '@/pages/storage/show'
import SubnetsListPage from '@/pages/subnets/list'
import SubnetShowPage from '@/pages/subnets/show'
import UserGroupCreatePage from '@/pages/user-groups/create'
import UserGroupsListPage from '@/pages/user-groups/list'
import PriceDetailPage from '@/pages/user-groups/price-detail'
import UserGroupShowPage from '@/pages/user-groups/show'
import UsersListPage from '@/pages/users/list'
import UserShowPage from '@/pages/users/show'
import VpcsListPage from '@/pages/vpcs/list'
import VpcShowPage from '@/pages/vpcs/show'
import { accessControlProvider } from '@/providers/accessControlProvider'
import { authProvider } from '@/providers/authProvider'
import { dataProvider } from '@/providers/dataProvider'

function MobileHeaderSpacer() {
  const screens = Grid.useBreakpoint()
  if (screens.lg) return null
  return <div style={{ height: 48 }} />
}

function FullCenter({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  )
}

function LoginRedirect() {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    saveReturnTo(location.pathname, location.search)
    void navigate('/login', { replace: true })
  }, [location, navigate])

  return null
}

function AdminGate({ children }: { children: ReactNode }) {
  const { data: permissions, isLoading } = usePermissions<string[]>({})

  if (isLoading) {
    return (
      <FullCenter>
        <Spin size="large" />
      </FullCenter>
    )
  }
  if (!hasAdminAccess(permissions ?? [])) return <AccessDeniedPage />
  return <>{children}</>
}

function RoleAccessDenied() {
  return (
    <AccessDeniedPage description="Your signed-in account does not have permission to access role management." />
  )
}

function GpuAccessDenied() {
  return (
    <AccessDeniedPage description="Your signed-in account does not have permission to access the GPU service." />
  )
}

function guarded(
  resource: string,
  action: string,
  page: ReactNode,
  denied: ReactNode,
) {
  return (
    <CanAccess resource={resource} action={action} fallback={denied}>
      {page}
    </CanAccess>
  )
}

const gpuRoutes = (
  <>
    <Route
      path="/gpu/providers"
      element={guarded(
        'gpu-providers',
        'list',
        <GpuProvidersListPage />,
        <GpuAccessDenied />,
      )}
    />
    <Route
      path="/gpu/providers/:key"
      element={guarded(
        'gpu-providers',
        'show',
        <GpuProviderShowPage />,
        <GpuAccessDenied />,
      )}
    />
    <Route
      path="/gpu/instances"
      element={guarded(
        'gpu-instances',
        'list',
        <GpuInstancesListPage />,
        <GpuAccessDenied />,
      )}
    />
    <Route
      path="/gpu/instances/:id"
      element={guarded(
        'gpu-instances',
        'show',
        <GpuInstanceShowPage />,
        <GpuAccessDenied />,
      )}
    />
    <Route
      path="/gpu/akash-blocks"
      element={guarded(
        'gpu-akash-blocks',
        'list',
        <AkashBlocksListPage />,
        <GpuAccessDenied />,
      )}
    />
    <Route
      path="/gpu/reports"
      element={guarded(
        'gpu-reports',
        'list',
        <GpuReportsListPage />,
        <GpuAccessDenied />,
      )}
    />
  </>
)

const resources = [
  {
    name: 'dashboard',
    list: '/',
    meta: { label: 'Dashboard', icon: <DashboardOutlined /> },
  },
  {
    name: 'business',
    list: '/business',
    meta: { label: 'Capacity & revenue', icon: <BarChartOutlined /> },
  },
  {
    name: 'users-section',
    meta: { label: 'Users', icon: <TeamOutlined /> },
  },
  {
    name: 'users',
    list: '/users',
    show: '/users/:id',
    meta: {
      label: 'Users',
      icon: <TeamOutlined />,
      parent: 'users-section',
    },
  },
  {
    name: 'user-groups',
    list: '/user-groups',
    create: '/user-groups/create',
    show: '/user-groups/:id',
    meta: {
      label: 'User Groups',
      icon: <UsergroupAddOutlined />,
      parent: 'users-section',
    },
  },
  {
    name: 'invitations',
    list: '/invitations',
    meta: {
      label: 'Invitations',
      icon: <MailOutlined />,
      parent: 'users-section',
    },
  },
  {
    name: 'roles',
    list: '/roles',
    create: '/roles/create',
    show: '/roles/:id',
    meta: {
      label: 'Roles & Access',
      icon: <KeyOutlined />,
      parent: 'users-section',
    },
  },
  {
    name: 'cloud-section',
    meta: { label: 'Public Cloud', icon: <CloudServerOutlined /> },
  },
  {
    name: 'vms',
    list: '/compute',
    show: '/compute/:id',
    meta: {
      label: 'Compute',
      icon: <CloudServerOutlined />,
      parent: 'cloud-section',
    },
  },
  {
    name: 'images',
    list: '/images',
    meta: {
      label: 'Images',
      icon: <DatabaseOutlined />,
      parent: 'cloud-section',
    },
  },
  {
    name: 'storages',
    list: '/storage',
    show: '/storage/:id',
    meta: {
      label: 'Storage',
      icon: <DatabaseOutlined />,
      parent: 'cloud-section',
    },
  },
  {
    name: 'public-ips',
    list: '/network',
    show: '/network/:id',
    meta: {
      label: 'Network',
      icon: <GlobalOutlined />,
      parent: 'cloud-section',
    },
  },
  {
    name: 'vpcs',
    list: '/vpcs',
    show: '/vpcs/:id',
    meta: {
      label: 'VPCs',
      icon: <LinkOutlined />,
      parent: 'cloud-section',
    },
  },
  {
    name: 'subnets',
    list: '/subnets',
    show: '/subnets/:id',
    meta: {
      label: 'Subnets',
      icon: <NodeIndexOutlined />,
      parent: 'cloud-section',
    },
  },
  {
    name: 'clusters',
    list: '/clusters',
    show: '/clusters/:id',
    meta: {
      label: 'Clusters',
      icon: <ClusterOutlined />,
      parent: 'cloud-section',
    },
  },
  {
    name: 'gpu-section',
    meta: { label: 'GPU', icon: <ThunderboltOutlined /> },
  },
  {
    name: 'gpu-providers',
    list: '/gpu/providers',
    show: '/gpu/providers/:key',
    meta: {
      label: 'Providers',
      icon: <ThunderboltOutlined />,
      parent: 'gpu-section',
    },
  },
  {
    name: 'gpu-instances',
    list: '/gpu/instances',
    show: '/gpu/instances/:id',
    meta: {
      label: 'Instances',
      icon: <CloudServerOutlined />,
      parent: 'gpu-section',
    },
  },
  {
    name: 'gpu-akash-blocks',
    list: '/gpu/akash-blocks',
    meta: {
      label: 'Akash blocks',
      icon: <StopOutlined />,
      parent: 'gpu-section',
    },
  },
  {
    name: 'gpu-reports',
    list: '/gpu/reports',
    meta: {
      label: 'Reports',
      icon: <FileExclamationOutlined />,
      parent: 'gpu-section',
    },
  },
  {
    name: 'billing-section',
    meta: { label: 'Billing', icon: <DollarOutlined /> },
  },
  {
    name: 'payments',
    list: '/payments',
    show: '/payments/:id',
    meta: {
      label: 'Payments',
      icon: <CreditCardOutlined />,
      parent: 'billing-section',
    },
  },
  {
    name: 'payment-methods',
    list: '/payment-methods',
    meta: {
      label: 'Payment Methods',
      icon: <WalletOutlined />,
      parent: 'billing-section',
    },
  },
  {
    name: 'prices',
    list: '/prices',
    meta: {
      label: 'Prices',
      icon: <DollarOutlined />,
      parent: 'billing-section',
    },
  },
  {
    name: 'marketing-section',
    meta: { label: 'Marketing', icon: <GiftOutlined /> },
  },
  {
    name: 'promo-codes',
    list: '/promo-codes',
    show: '/promo-codes/:id',
    meta: {
      label: 'Promo Codes',
      icon: <GiftOutlined />,
      parent: 'marketing-section',
    },
  },
]

export default function App() {
  const { mode } = useThemeMode()

  return (
    <BrowserRouter>
      <ConfigProvider theme={getThemeConfig(mode)}>
        <AntApp>
          <Refine
            routerProvider={routerProvider}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            dataProvider={dataProvider}
            notificationProvider={useNotificationProvider}
            resources={resources}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              // Fail fast for an admin tool — avoid masking errors.
              reactQuery: {
                clientConfig: {
                  defaultOptions: {
                    queries: { retry: 1 },
                    mutations: { retry: false },
                  },
                },
              },
            }}
          >
            <Routes>
              {/* Auth callback — no layout */}
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Login — no layout */}
              <Route
                path="/login"
                element={
                  <Authenticated key="login-guard" fallback={<LoginPage />}>
                    <NavigateToResource resource="dashboard" />
                  </Authenticated>
                }
              />

              {/* Protected routes */}
              <Route
                element={
                  <Authenticated key="auth-guard" fallback={<LoginRedirect />}>
                    <AdminGate>
                      <ThemedLayout
                        Sider={() => <CustomSider />}
                        Header={() => <MobileHeaderSpacer />}
                      >
                        <Outlet />
                      </ThemedLayout>
                    </AdminGate>
                  </Authenticated>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="/business" element={<BusinessPage />} />
                <Route path="/users" element={<UsersListPage />} />
                <Route path="/users/:id" element={<UserShowPage />} />
                <Route path="/user-groups" element={<UserGroupsListPage />} />
                <Route
                  path="/user-groups/create"
                  element={<UserGroupCreatePage />}
                />
                <Route
                  path="/user-groups/:id"
                  element={<UserGroupShowPage />}
                />
                <Route
                  path="/user-groups/:id/prices/:resourceType/:comboKey"
                  element={<PriceDetailPage />}
                />
                <Route path="/invitations" element={<InvitationsListPage />} />
                <Route
                  path="/roles"
                  element={guarded(
                    'roles',
                    'list',
                    <RolesListPage />,
                    <RoleAccessDenied />,
                  )}
                />
                <Route
                  path="/roles/create"
                  element={guarded(
                    'roles',
                    'create',
                    <RoleCreatePage />,
                    <RoleAccessDenied />,
                  )}
                />
                <Route
                  path="/roles/:id"
                  element={guarded(
                    'roles',
                    'show',
                    <RoleShowPage />,
                    <RoleAccessDenied />,
                  )}
                />
                <Route path="/compute" element={<ComputeListPage />} />
                <Route path="/compute/:id" element={<ComputeShowPage />} />
                <Route path="/images" element={<ImagesRoute />} />
                <Route path="/storage" element={<StorageListPage />} />
                <Route path="/storage/:id" element={<StorageShowPage />} />
                <Route path="/network" element={<NetworkListPage />} />
                <Route path="/network/:id" element={<NetworkShowPage />} />
                <Route path="/vpcs" element={<VpcsListPage />} />
                <Route path="/vpcs/:id" element={<VpcShowPage />} />
                <Route path="/subnets" element={<SubnetsListPage />} />
                <Route path="/subnets/:id" element={<SubnetShowPage />} />
                <Route path="/clusters" element={<ClustersListPage />} />
                <Route path="/clusters/:id" element={<ClusterShowPage />} />
                {gpuRoutes}
                <Route path="/payments" element={<PaymentsListPage />} />
                <Route path="/payments/:id" element={<PaymentShowPage />} />
                <Route
                  path="/payment-methods"
                  element={<PaymentMethodsListPage />}
                />
                <Route path="/prices" element={<PricesListPage />} />
                <Route path="/promo-codes" element={<PromoCodesListPage />} />
                <Route
                  path="/promo-codes/:id"
                  element={<PromoCodeShowPage />}
                />
              </Route>

              {/* 404 */}
              <Route path="*" element={<ErrorComponent />} />
            </Routes>
            <UnsavedChangesNotifier />
            <CommandPalette />
            <DocumentTitleHandler handler={getDocumentTitle} />
          </Refine>
        </AntApp>
      </ConfigProvider>
    </BrowserRouter>
  )
}
